import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { updateList } from "@/mocks/updates";
import { newsList } from "@/mocks/news";
import useDocumentTitle from "@/hooks/useDocumentTitle";
import NewsCard from "@/components/feature/NewsCard";
import Breadcrumb from "@/components/base/Breadcrumb";
import EmptyState from "@/components/base/EmptyState";

export default function UpdateDetail() {
  const { id } = useParams();
  const anime = updateList.find((u) => u.id === id);
  useDocumentTitle(
    anime ? `${anime.title} 动画更新 - 漫蛙2` : "作品不存在 - 漫蛙2",
    anime ? anime.description : "该作品不存在或已下架。"
  );
  const [selected, setSelected] = useState(0);

  const currentEpisode = anime?.episodes[selected];

  const relatedNews = useMemo(() => {
    if (!anime) return [];
    const matched = newsList.filter((n) => anime.genre.some((g) => n.tags.some((t) => t.includes(g))));
    const rest = newsList.filter((n) => !matched.includes(n));
    return [...matched, ...rest].slice(0, 3);
  }, [anime]);

  if (!anime) {
    return (
      <div className="page-wrap py-16">
        <EmptyState
          icon="ri-emotion-sad-line"
          title="没有找到这部作品"
          description="作品可能已下架或链接有误，返回更新列表看看其他在番作品吧。"
          actionLabel="返回更新列表"
          actionTo="/updates"
        />
      </div>
    );
  }

  return (
    <div className="pb-4">
      <div className="page-wrap py-6 md:py-8">
        <Breadcrumb items={[{ label: "动画更新", to: "/updates" }, { label: anime.title }]} />
      </div>

      <section className="page-wrap grid grid-cols-1 gap-8 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <div className="relative overflow-hidden rounded-lg bg-secondary-900">
            <img
              src={anime.cover}
              alt={`${anime.title} 封面`}
              title={`${anime.title} 动画更新 - 漫蛙2`}
              className="h-56 w-full object-cover object-top md:h-72"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-secondary-950/85 to-transparent" />
            <div className="absolute bottom-0 left-0 w-full p-5">
              <div className="flex flex-wrap items-center gap-2">
                {anime.genre.map((g) => (
                  <span key={g} className="rounded-full bg-primary-500/90 px-2.5 py-0.5 text-xs font-medium text-background-50">
                    {g}
                  </span>
                ))}
              </div>
              <h1 className="mt-2 text-2xl font-extrabold text-background-50 md:text-3xl">{anime.title}</h1>
            </div>
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-foreground-600">
            <span className="flex items-center gap-1.5">
              <i className="ri-play-list-2-line text-primary-600"></i>
              更新至第 <strong className="text-primary-600">{anime.episode}</strong> 集 / 全 {anime.totalEpisodes} 集
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-time-line"></i>
              {anime.updatedAt}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-2-line"></i>
              {anime.weekday}更新
            </span>
          </div>

          <div className="mt-6 rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="text-base font-bold text-foreground-950">剧情简介</h2>
            <p className="mt-3 text-[15px] leading-[1.9] text-foreground-800">{anime.description}</p>

            {currentEpisode && (
              <div className="mt-5 rounded-md bg-background-100 p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h3 className="text-sm font-semibold text-foreground-950">{currentEpisode.title}</h3>
                  <span
                    className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${
                      currentEpisode.status === "updated"
                        ? "bg-primary-100 text-primary-700"
                        : "bg-background-200 text-foreground-500"
                    }`}
                  >
                    {currentEpisode.status === "updated" ? "已更新" : "未更新"}
                  </span>
                </div>
                <p className="mt-2 text-xs text-foreground-500">更新时间：{currentEpisode.date}</p>
                <p className="mt-2 text-sm leading-relaxed text-foreground-700">
                  当前选中的是 {currentEpisode.title}。本站仅提供更新信息展示，不提供在线播放。
                </p>
              </div>
            )}
          </div>

          <section className="mt-8">
            <h2 className="text-lg font-bold text-foreground-950">剧集列表</h2>
            <p className="mt-1 text-sm text-foreground-500">点击剧集可查看该集的更新状态（不含播放功能）。</p>
            <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
              {anime.episodes.map((ep, index) => (
                <button
                  key={ep.no}
                  type="button"
                  onClick={() => setSelected(index)}
                  className={`flex items-center justify-between gap-3 rounded-md border px-4 py-3 text-left transition-colors ${
                    index === selected
                      ? "border-primary-400 bg-primary-50"
                      : "border-background-200 bg-background-50 hover:border-primary-300"
                  }`}
                >
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-medium text-foreground-900">{ep.title}</span>
                    <span className="mt-0.5 block text-xs text-foreground-500">{ep.date}</span>
                  </span>
                  <span
                    className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ${
                      ep.status === "updated" ? "bg-primary-100 text-primary-700" : "bg-background-200 text-foreground-500"
                    }`}
                  >
                    {ep.status === "updated" ? "已更新" : "未更新"}
                  </span>
                </button>
              ))}
            </div>
          </section>
        </div>

        <aside className="lg:col-span-1">
          <h2 className="text-lg font-bold text-foreground-950">相关资讯推荐</h2>
          <div className="mt-4 flex flex-col gap-3">
            {relatedNews.map((item) => (
              <NewsCard key={item.id} item={item} layout="horizontal" />
            ))}
          </div>
          <Link
            to="/news"
            className="mt-4 inline-flex items-center gap-1 whitespace-nowrap text-sm font-medium text-primary-600 hover:text-primary-700"
          >
            浏览更多资讯
            <i className="ri-arrow-right-line"></i>
          </Link>
        </aside>
      </section>
    </div>
  );
}